import { zodResolver } from "@hookform/resolvers/zod";
import {
  AriaTab,
  AriaTabList,
  AriaTabPanel,
  AriaTabs,
  KdsText,
  Modal,
} from "@kazaar-platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { findIndex } from "lodash";
import { useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { type AltaviaConnectGetQuoteFromAwsReadDto } from "@/config/apiClient";
import { OrderItemStatusEnum } from "@/config/apiClient.types.ts";
import { base64ToFile } from "@/helpers/base64ToFile.ts";
import {
  useAwsConnect,
  useDirectUpload,
  useOrderItemQuotes,
  useToasts,
} from "@/hooks";
import {
  type InferredOrderDetailsAddQuoteFormPageSchema,
  orderDetailsAddQuoteFormPageSchema,
} from "@/pages/OrderDetailsAddQuoteFormPage/OrderDetailsAddQuoteFormPage.schema";
import { tolgee } from "@/providers/TranslationProvider";
import { AddQuoteOrderItemStep } from "./_partials/AddQuoteOrderItemStep";
import { AddQuoteQuoteStep } from "./_partials/AddQuoteQuoteStep";
import { AddQuoteSearchQuoteStep } from "./_partials/AddQuoteSearchQuoteStep";
import {
  type GetOrderItemStepProps,
  type GetQuoteStepProps,
  type GetSearchQuoteStepProps,
  type ModalAddQuoteProps,
} from "./ModalAddQuote.types";

const getTabsData = (
  orderItemStepProps: GetOrderItemStepProps,
  quoteStepProps: GetQuoteStepProps,
  searchQuoteStepProps: GetSearchQuoteStepProps
) => {
  return [
    {
      key: "searchQuote",
      title: tolgee.t("oms.addQuote.stepper.searchQuoteStep"),
      tabPanel: <AddQuoteSearchQuoteStep {...searchQuoteStepProps} />,
      fields: ["quoteNumber"],
    },
    {
      key: "quote",
      title: tolgee.t("oms.addQuote.stepper.quoteStep"),
      tabPanel: <AddQuoteQuoteStep {...quoteStepProps} />,
      fields: ["totalPrice", "fileSignedId"],
    },
    {
      key: "orderItems",
      title: tolgee.t("oms.addQuote.stepper.orderItemsStep"),
      tabPanel: <AddQuoteOrderItemStep {...orderItemStepProps} />,
      fields: ["sandboxItemIds"],
    },
  ] as const;
};

export const ModalAddQuote = ({
  orderItem,
  onCloseModal,
}: ModalAddQuoteProps) => {
  const [rawAwsQuote, setRawAwsQuote] =
    useState<AltaviaConnectGetQuoteFromAwsReadDto["rawAwsQuote"]>(undefined);
  const { t } = useTranslate();
  const { add } = useToasts();

  // Create Quote Hook
  const { useAddOrderItemsQuote } = useOrderItemQuotes();
  const { useGetQuoteFromAws } = useAwsConnect();
  const { createFileUploadUrl, uploadFile } = useDirectUpload();

  const {
    mutateAsync: addOrderItemsQuote,
    isPending: isCreatingSandboxItemQuotePending,
  } = useAddOrderItemsQuote();

  // Define Form
  const methods = useForm<InferredOrderDetailsAddQuoteFormPageSchema>({
    criteriaMode: "all",
    mode: "all",
    resolver: zodResolver(orderDetailsAddQuoteFormPageSchema),
    defaultValues: {
      quoteNumber: "",
      totalPrice: undefined,
      fileSignedId: undefined,
      sandboxItemIds: [orderItem.sandboxItemId],
    },
  });
  const {
    mutateAsync: fetchQuoteFromAws,
    isPending: isGettingQuotFromAwsPending,
  } = useGetQuoteFromAws();

  const [currentStep, setCurrentStep] =
    useState<ReturnType<typeof getTabsData>[number]["key"]>("searchQuote");
  const [fileFromAws, setFileFromAws] = useState<File | undefined>(undefined);
  const [hasCommonProduct, setHasCommonProduct] = useState<boolean | undefined>(
    undefined
  );
  const queryClient = useQueryClient();

  // Go Next Step
  const goNextStep = async () => {
    const currentStepData = stepTabs.find((tab) => tab.key === currentStep);

    const isValid = await methods.trigger(currentStepData?.fields || []);

    if (!isValid) {
      return;
    }

    const nextStep = stepTabs[findIndex(stepTabs, { key: currentStep }) + 1];

    if (nextStep.key === "quote") {
      try {
        const response = await fetchQuoteFromAws({
          quoteNumber: methods.getValues("quoteNumber"),
          orderId: orderItem.orderId,
        });
        if (response?.status === 200 && response.data.products) {
          methods.setValue("totalPrice", response.data.amount as number);
          const quoteFile: File = base64ToFile({
            base64String: response.data.pdf as string,
            fileName: methods.getValues("quoteNumber"),
            mimeType: "application/pdf",
          });

          // Return in case of MyMarket flow only
          if (response.data.rawAwsQuote) {
            setRawAwsQuote(response.data.rawAwsQuote);
          }

          const uploadDetails = await createFileUploadUrl(
            "sandboxItemQuote.quote",
            quoteFile
          );
          await uploadFile(quoteFile, uploadDetails, () => {
            return undefined;
          });

          methods.setValue("fileSignedId", uploadDetails.fileSignedId);

          methods.setValue("sandboxItemIds", [
            ...orderItem.order.OrderItem.filter(
              (oi) =>
                // Check if order item is in the raw quote
                response?.data?.rawAwsQuote?.products
                  ?.map((e) => e.externalCode)
                  .includes(oi.id) &&
                !oi.sandboxItemQuoteId &&
                oi.status !== OrderItemStatusEnum.CANCELLED &&
                !oi.sandboxItem.SandboxItemQuote.some((q) => !!q.sentAt)
            ).map((oi) => oi.sandboxItemId),
          ]);

          setFileFromAws(quoteFile);
          setHasCommonProduct(true);
          setCurrentStep(nextStep.key);
        }
      } catch (err) {
        if (
          isAxiosError(err) &&
          err?.response?.data?.cause?.type ===
            "unableToFindQuoteForTheGivenOrder"
        ) {
          methods.reset();
          setHasCommonProduct(false);
          setFileFromAws(undefined);
        } else {
          setCurrentStep(nextStep.key);
        }
      }
    } else {
      setCurrentStep(nextStep.key);
    }
  };

  // Go Previous Step
  const goPreviousStep = () => {
    const previousStep =
      stepTabs[findIndex(stepTabs, { key: currentStep }) - 1];
    setCurrentStep(previousStep.key);
  };
  // Redefine TabsData with Props
  const stepTabs = getTabsData(
    {
      orderItems: orderItem?.order.OrderItem || [],
      currentSandboxItemId: orderItem?.sandboxItemId,
      isFromAws: hasCommonProduct,
    },
    {
      fileFromAws: fileFromAws || undefined,
      goNextStep: () => void goNextStep(),
    },
    {
      hasCommonProduct: hasCommonProduct,
      goNextStep: () => void goNextStep(),
    }
  );

  // Reset Form and Stepper on Modal Close
  useEffect(() => {
    methods.reset();
    setCurrentStep("searchQuote");
  }, [methods]);

  // Handle Submit
  const onSubmit: SubmitHandler<
    InferredOrderDetailsAddQuoteFormPageSchema
  > = async (data) => {
    const res = await addOrderItemsQuote({
      command: {
        fileSignedId: data.fileSignedId,
        quoteNumber: data.quoteNumber,
        sandboxItemsIds: data.sandboxItemIds,
        totalPrice: data.totalPrice * 100,
        // For MyMarket feature only (no flag check needed)
        awsQuote: rawAwsQuote,
      },
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
    });

    if (res.status === 200 || res.status === 201) {
      onCloseModal();
      queryClient.invalidateQueries({
        queryKey: ["order", orderItem.orderId],
      });
      queryClient.invalidateQueries({
        queryKey: ["orderItem", orderItem.orderId, orderItem.id],
      });
      add({
        state: "success",
        title: t("oms.addQuote.stepper.toastSaveSuccess"),
      });
    } else {
      // Display error toast
      add({
        state: "error",
        title: t("toast.form.error"),
      });
    }
  };

  // handle Submit Error
  const onSubmitError = () => {
    if (methods.getValues("quoteNumber")) {
      setCurrentStep("quote");
    } else {
      setCurrentStep("searchQuote");
    }
    void methods.trigger();
    add({
      state: "error",
      title: t("toast.form.error"),
    });
  };

  return (
    <FormProvider {...methods}>
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <Modal
              isOpen
              title={t("oms.addQuote.modalTitle")}
              contentClassName="!p-0"
              headerClassName="border-none"
              onOpenChange={onCloseModal}
              actionButtonsLeft={[
                {
                  variant: "secondary",
                  label: t("buttons.cancel"),
                  size: "l",
                  onPress: onCloseModal,
                  isLoading:
                    currentStep !== "orderItems"
                      ? isGettingQuotFromAwsPending
                      : isCreatingSandboxItemQuotePending,
                  isDisabled: ctx.isUploading,
                },
              ]}
              actionButtonsRight={[
                ...(currentStep !== "searchQuote"
                  ? [
                      {
                        variant: "secondary" as
                          | "destructive"
                          | "primary"
                          | "secondary"
                          | "tertiary"
                          | "gradient"
                          | "ghost"
                          | undefined,
                        label: t("buttons.previous"),
                        size: "l" as "s" | "m" | "l" | undefined,
                        onPress: goPreviousStep,
                        isDisabled: ctx.isUploading,
                      },
                    ]
                  : []),
                {
                  label:
                    currentStep !== "orderItems"
                      ? t("buttons.next")
                      : t("buttons.addThatQuote"),
                  size: "l",
                  isLoading:
                    currentStep !== "orderItems"
                      ? isGettingQuotFromAwsPending
                      : isCreatingSandboxItemQuotePending,
                  isDisabled: ctx.isUploading,
                  type: currentStep === "orderItems" ? "submit" : "button",
                  dataCy:
                    currentStep === "orderItems"
                      ? "submitQuoteButton"
                      : "quoteStepNextButton",
                  onPress: () => {
                    if (currentStep !== "orderItems") {
                      void goNextStep();
                    } else {
                      void methods.handleSubmit(onSubmit, onSubmitError)();
                    }
                  },
                },
              ]}
            >
              <div className="h-fit flex-row-reverse flex-wrap gap-10 md:flex-nowrap">
                <div className="flex h-full w-full flex-col gap-6 md:flex-row-reverse">
                  <AriaTabs
                    selectedKey={currentStep}
                    className="grid flex-1 auto-rows-min grid-cols-12 gap-x-4"
                    onSelectionChange={(tabKey) =>
                      setCurrentStep(
                        tabKey as "quote" | "orderItems" | "searchQuote"
                      )
                    }
                  >
                    <AriaTabList
                      aria-label="New configuration steps"
                      className="col-span-12 flex flex-row items-center"
                    >
                      {stepTabs.map((tab, index) => {
                        return (
                          <AriaTab
                            key={tab.key}
                            id={tab.key}
                            isDisabled
                            className={({ isSelected }) =>
                              `${
                                isSelected
                                  ? "border-primary"
                                  : "border-slate-300"
                              } text-slate flex w-full cursor-pointer flex-row items-center justify-center gap-4 border-b-2 pb-2 pl-8 focus:outline-none md:justify-start`
                            }
                          >
                            {({ isSelected }) => (
                              <div className="hidden flex-col md:flex">
                                <KdsText
                                  size="subtitle-sm"
                                  color={
                                    isSelected
                                      ? "primary-text"
                                      : "text-bodyvariant"
                                  }
                                >
                                  {t(
                                    "order.catalogMediumConfiguration.stepper.step",
                                    { value: index + 1 }
                                  )}
                                </KdsText>

                                <KdsText
                                  color={
                                    isSelected
                                      ? "primary-text"
                                      : "text-bodyvariant"
                                  }
                                  size="subtitle-sm"
                                  truncate
                                >
                                  {tab.title}
                                </KdsText>
                              </div>
                            )}
                          </AriaTab>
                        );
                      })}
                    </AriaTabList>

                    {stepTabs.map((tab) => (
                      <AriaTabPanel
                        key={tab.key}
                        id={tab.key}
                        className="col-span-12 flex flex-col"
                      >
                        {tab.tabPanel}
                      </AriaTabPanel>
                    ))}
                  </AriaTabs>
                </div>
              </div>
            </Modal>
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </FormProvider>
  );
};
